// src/pages/PartTypes/PartTypes.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { partTypeService } from '../../services/api';
import PartTypeForm from './PartTypeForm';

const PartTypes = () => {
    const [partTypes, setPartTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPartType, setCurrentPartType] = useState(null);

    useEffect(() => {
        fetchPartTypes();
    }, []);

    const fetchPartTypes = async () => {
        try {
            setLoading(true);
            const response = await partTypeService.getPartTypes();
            console.log(response);
            setPartTypes(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching part types:', err);
            setError('Failed to load part types. Please try again later.');
            toast.error('Failed to load part types');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (partType = null) => {
        setCurrentPartType(partType);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setCurrentPartType(null);
        setIsModalOpen(false);
    };

    const handleSave = async (formData) => {
        try {
            if (currentPartType) {
                // Update existing part type
                await partTypeService.updatePartType(currentPartType.id, formData);
                toast.success('Part type updated successfully');
            } else {
                // Create new part type
                await partTypeService.createPartType(formData);
                toast.success('Part type created successfully');
            }
            fetchPartTypes();
            handleCloseModal();
        } catch (err) {
            console.error('Error saving part type:', err);
            toast.error(err.response?.data?.errors ? Object.values(err.response.data.errors).flat().join(', ') : 'Error saving part type');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this part type? This action cannot be undone.')) {
            return;
        }

        try {
            await partTypeService.deletePartType(id);
            toast.success('Part type deleted successfully');
            fetchPartTypes();
        } catch (err) {
            console.error('Error deleting part type:', err);
            toast.error('Failed to delete part type');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold text-gray-800">Part Types</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <PlusIcon className="h-5 w-5 mr-2" /> Add Part Type
                </button>
            </div>

            {/* Mobile View (Card Layout) */}
            <div className="md:hidden space-y-4">
                {partTypes.length > 0 ? (
                    partTypes.map((partType) => (
                        <div key={partType.id} className="bg-white shadow overflow-hidden rounded-lg">
                            <div className="px-4 py-5 sm:px-6">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">{partType.name}</h3>
                                <p className="mt-1 max-w-2xl text-sm text-gray-500">{partType.description || 'No description'}</p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {partType.is_sku_specific ? (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                            SKU-Specific
                                        </span>
                                    ) : (
                                         <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                            General
                                        </span>
                                     )}
                                    {partType.vendor && (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                            {partType.vendor.name}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="border-t border-gray-200 px-4 py-4">
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                                    <div className="col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">Slug</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{partType.slug || '—'}</dd>
                                    </div>
                                    <div className="col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">Type</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{partType.type || '—'}</dd>
                                    </div>
                                    <div className="col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">Current Stock</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{partType.inventory?.current_stock || 0}</dd>
                                    </div>
                                    <div className="col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">Threshold</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{partType.inventory?.threshold || 0}</dd>
                                    </div>
                                    {partType.vendor_sku && (
                                        <div className="col-span-1">
                                            <dt className="text-sm font-medium text-gray-500">Vendor SKU</dt>
                                            <dd className="mt-1 text-sm text-gray-900">{partType.vendor_sku}</dd>
                                        </div>
                                    )}
                                    {partType.vendor_price && (
                                        <div className="col-span-1">
                                            <dt className="text-sm font-medium text-gray-500">Vendor Price</dt>
                                            <dd className="mt-1 text-sm text-gray-900">${parseFloat(partType.vendor_price).toFixed(2)}</dd>
                                        </div>
                                    )}
                                </dl>
                                <div className="mt-4 flex justify-end space-x-3">
                                    <button
                                        onClick={() => handleOpenModal(partType)}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        <PencilIcon className="h-4 w-4 mr-1" /> Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(partType.id)}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                    >
                                        <TrashIcon className="h-4 w-4 mr-1" /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                     <div className="text-center py-10 bg-white shadow rounded-lg">
                         <p className="text-gray-500">No part types found. Add your first part type to get started.</p>
                     </div>
                 )}
            </div>

            {/* Desktop View (Table Layout) */}
            <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Slug
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            SKU Specific
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Vendor
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Current Stock
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Threshold
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {partTypes.length > 0 ? (
                        partTypes.map((partType) => (
                            <tr key={partType.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {partType.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {partType.description || 'No description'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {partType.slug || '—'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                    {partType.type.replace('_', ' ') || '—'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {partType.is_sku_specific ? (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                SKU-Specific
                                            </span>
                                    ) : (
                                         <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                General
                                            </span>
                                     )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {partType.vendor ? (
                                        <span className="text-sm">
                                            {partType.vendor.name}
                                            {partType.vendor_sku && <span className="ml-1 text-xs text-gray-500">({partType.vendor_sku})</span>}
                                        </span>
                                    ) : (
                                         <span className="text-gray-400">—</span>
                                     )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {partType?.current_stock || 0}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {partType?.threshold || 0}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handleOpenModal(partType)}
                                            className="text-indigo-600 hover:text-indigo-900"
                                        >
                                            <PencilIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(partType.id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                         <tr>
                             <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                                 No part types found.
                             </td>
                         </tr>
                     )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <PartTypeForm
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onSave={handleSave}
                    partType={currentPartType}
                />
            )}
        </div>
    );
};

export default PartTypes;
